// src/pages/analytics/AssistantTab.tsx
//
// Analytics Assistant — a conversational AI analyst. It answers questions
// about the tenant's data by running the same recipes the dashboards use.
// The agent does the reasoning + tool calls; this tab is a thin chat UI:
//
//   - getAnalystStatus() gates the whole thing (disabled => info panel only).
//   - Conversation lives in local state; each send POSTs the full history
//     (role + content only) to analystChat and swaps the pending bubble for
//     the reply (markdown answer + tool steps).
//   - Assistant answers are markdown — frequently a table — so we render them
//     with a small self-contained markdown->JSX renderer (no new dependency).
//     It handles headings, **bold**, `code`, bullet lists, and pipe tables.
//     Text is emitted as React string children (auto-escaped); no raw HTML.

import { useState, useRef, useEffect, Fragment, type ReactNode, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Sparkles, RefreshCw, Send, Trash2, ChevronDown, ChevronRight, AlertTriangle, Info, Wrench,
} from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import type { AnalystChatMessage, AnalystStep } from '../../types';

const STARTERS = [
  'Top 5 customers by sales this year',
  'Which customers stopped buying?',
  "What's my gross margin by category?",
  'A/R over 90 days by customer',
];

// ── Tiny markdown renderer ───────────────────────────────────────
// Block-level: ## headings, `- `/`* ` bullet lists, | pipe | tables, and
// paragraphs. Inline: **bold** and `code`. Everything renders as string
// children so React escapes it — no dangerouslySetInnerHTML, no raw HTML.

function parseInline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Tokenize on **bold** and `code`. Order matters: match either alternative.
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] != null) {
      nodes.push(<strong key={`${keyBase}-b${i}`} className="font-semibold text-white">{m[2]}</strong>);
    } else if (m[3] != null) {
      nodes.push(
        <code key={`${keyBase}-c${i}`} className="px-1 py-0.5 rounded bg-slate-950/60 border border-slate-700/50 text-[0.85em] font-mono text-slate-200">
          {m[3]}
        </code>,
      );
    }
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function isTableSeparator(line: string): boolean {
  // e.g. | --- | :---: | ---: |
  const cells = line.trim().replace(/^\||\|$/g, '').split('|');
  return cells.length > 0 && cells.every((c) => /^\s*:?-{2,}:?\s*$/.test(c));
}

function splitRow(line: string): string[] {
  return line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
}

function renderMarkdown(md: string): ReactNode {
  const lines = (md ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line — skip.
    if (line.trim() === '') { i++; continue; }

    // Heading (## / ### …). Treat # count as size.
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const cls = level <= 1
        ? 'text-base font-semibold text-white mt-2 mb-1'
        : level === 2
          ? 'text-sm font-semibold text-white mt-2 mb-1'
          : 'text-xs font-semibold text-slate-200 mt-2 mb-1';
      blocks.push(<div key={`h${key++}`} className={cls}>{parseInline(h[2], `h${key}`)}</div>);
      i++;
      continue;
    }

    // Table: current line looks like a row and the next line is a separator.
    if (line.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line);
      const bodyRows: string[][] = [];
      i += 2; // skip header + separator
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        bodyRows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push(
        <div key={`t${key++}`} className="my-2 overflow-x-auto rounded-lg border border-slate-700/50">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700/50 bg-slate-800/50">
                {header.map((c, ci) => (
                  <th key={ci} className="px-2.5 py-1.5 text-left text-slate-400 font-medium whitespace-nowrap">
                    {parseInline(c, `th${key}-${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri} className="border-b border-slate-800/50 last:border-0">
                  {header.map((_, ci) => (
                    <td key={ci} className="px-2.5 py-1.5 text-slate-200 tabular-nums whitespace-nowrap">
                      {parseInline(row[ci] ?? '', `td${key}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Bullet list.
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={`u${key++}`} className="my-1.5 ml-4 list-disc space-y-0.5 text-slate-200">
          {items.map((it, ii) => (
            <li key={ii}>{parseInline(it, `li${key}-${ii}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Paragraph — gather consecutive plain lines.
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !(lines[i].includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1]))
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={`p${key++}`} className="my-1.5 leading-relaxed text-slate-200">
        {para.map((ln, li) => (
          <Fragment key={li}>
            {li > 0 && <br />}
            {parseInline(ln, `p${key}-${li}`)}
          </Fragment>
        ))}
      </p>,
    );
  }

  return <div className="text-sm">{blocks}</div>;
}

// ── Steps disclosure ─────────────────────────────────────────────

function StepsDisclosure({ steps }: { steps: AnalystStep[] }) {
  const [open, setOpen] = useState(false);
  if (!steps || steps.length === 0) return null;
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300 transition-colors"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Wrench size={11} />
        {steps.length} report{steps.length === 1 ? '' : 's'} run
      </button>
      {open && (
        <ol className="mt-1.5 ml-1 space-y-1 border-l border-slate-700/50 pl-3">
          {steps.map((s, i) => (
            <li key={i} className="text-[11px]">
              <span className={`font-mono ${s.isError ? 'text-red-400' : 'text-slate-300'}`}>{s.tool}</span>
              {s.detail && <span className={`ml-1.5 ${s.isError ? 'text-red-300/80' : 'text-slate-500'}`}>{s.detail}</span>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ── Message bubbles ──────────────────────────────────────────────

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 px-3.5 py-2 text-sm text-white whitespace-pre-wrap break-words">
        {text}
      </div>
    </div>
  );
}

function AssistantBubble({ msg }: { msg: AnalystChatMessage }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-slate-800/60 border border-slate-700/40 px-3.5 py-2.5 text-slate-200 break-words">
        {msg.pending ? (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <RefreshCw size={14} className="animate-spin" />
            <span>Thinking…</span>
          </div>
        ) : msg.error ? (
          <div className="flex items-start gap-2 text-sm text-red-300">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>{msg.content}</span>
          </div>
        ) : (
          <>
            {renderMarkdown(msg.content)}
            {msg.steps && msg.steps.length > 0 && <StepsDisclosure steps={msg.steps} />}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main tab ─────────────────────────────────────────────────────

export function AssistantTab() {
  const statusQ = useQuery({
    queryKey: ['analyst', 'status'],
    queryFn: () => api.getAnalystStatus(),
    staleTime: 5 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const [messages, setMessages] = useState<AnalystChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Autoscroll to the newest message.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  async function send(text: string) {
    const content = text.trim();
    if (!content || sending) return;

    // Full history sent to the agent (role + content only).
    const history = [...messages, { role: 'user' as const, content }];
    const wire = history.map((m) => ({ role: m.role, content: m.content }));

    setMessages([
      ...history,
      { role: 'assistant', content: '', pending: true },
    ]);
    setInput('');
    setSending(true);

    try {
      const reply = await api.analystChat(wire);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: reply.answer,
          steps: reply.steps,
        };
        return next;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          content: `Sorry — I couldn't answer that. ${message}`,
          error: true,
        };
        return next;
      });
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  }

  const header = (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Sparkles size={20} /> Analytics Assistant
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Ask about your sales, customers, inventory, receivables, or financials — answers come straight from your live reports.
        </p>
      </div>
      {messages.length > 0 && (
        <button
          onClick={() => { if (!sending) setMessages([]); }}
          disabled={sending}
          title="Clear the conversation"
          className="shrink-0 px-2.5 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40"
        >
          <Trash2 size={12} /> Clear
        </button>
      )}
    </div>
  );

  // Loading status.
  if (statusQ.isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      </div>
    );
  }

  // Disabled on this tenant's agent — info panel, no chat box.
  if (!statusQ.data?.enabled) {
    return (
      <div className="space-y-4">
        {header}
        <div className="p-4 rounded-xl bg-blue-950/30 border border-blue-900/40 text-blue-100 text-sm flex items-start gap-3 max-w-2xl">
          <Info size={18} className="mt-0.5 shrink-0 text-blue-300" />
          <div>
            <div className="font-medium">The AI assistant isn't enabled on this tenant's agent.</div>
            <div className="text-blue-200/80 mt-1">
              Ask your administrator to set <code className="px-1 py-0.5 rounded bg-blue-950/60 border border-blue-800/50 font-mono text-[0.85em]">Analyst:Enabled</code> and an Anthropic API key in the agent config.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-180px)] min-h-[420px] gap-3">
      {header}

      {/* Message list */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3 text-slate-500">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/20 flex items-center justify-center">
              <Sparkles size={22} className="text-blue-300" />
            </div>
            <p className="text-sm max-w-md">
              Ask a question about your business data. I'll run the relevant reports and answer with the numbers.
            </p>
          </div>
        ) : (
          messages.map((m, i) =>
            m.role === 'user'
              ? <UserBubble key={i} text={m.content} />
              : <AssistantBubble key={i} msg={m} />,
          )
        )}
      </div>

      {/* Starter chips (only when empty) */}
      {messages.length === 0 && (
        <div className="flex flex-wrap gap-2">
          {STARTERS.map((s) => (
            <button
              key={s}
              onClick={() => void send(s)}
              disabled={sending}
              className="px-3 py-1.5 text-xs rounded-full border border-slate-700/50 text-slate-300 hover:text-white hover:border-blue-500/40 hover:bg-blue-500/10 bg-slate-900/40 transition-all disabled:opacity-40"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <div className="flex items-end gap-2 rounded-xl border border-slate-700/50 bg-slate-800/40 p-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={sending}
          rows={1}
          placeholder="Ask about your sales, customers, inventory, receivables…"
          className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-white placeholder-slate-500 focus:outline-none max-h-40 disabled:opacity-60"
        />
        <button
          onClick={() => void send(input)}
          disabled={sending || input.trim().length === 0}
          title="Send (Enter)"
          className="shrink-0 p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-all disabled:opacity-40 disabled:hover:bg-blue-600"
        >
          {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
      <p className="text-[10px] text-slate-600 -mt-1 px-1">
        Enter to send · Shift+Enter for a new line
      </p>
    </div>
  );
}
